import { useState, useEffect } from 'react';
import { client } from '../../sdkAdapter';
import Swal from 'sweetalert2';
import {
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableRow,
} from "../ui/table";
import { Modal } from "../ui/modal";
import Badge from "../ui/badge/Badge";

export default function SettingsCurrencies() {
    const [currencies, setCurrencies] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);

    const [formData, setFormData] = useState({
        name: '',
        symbol: '',
        active: true
    });

    const refreshData = async () => {
        setLoading(true);
        try {
            const res = await client.getApiSettingsCurrencies() as any;
            const data = res.data?.data?.data || res.data?.data || res.data || [];
            setCurrencies(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Failed to fetch currencies:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        refreshData();
    }, []);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        const checked = (e.target as HTMLInputElement).checked;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : (name === 'active' ? value === '1' : value)
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.symbol) {
            Swal.fire({
                title: 'Validation Error',
                text: 'Currency Symbol is required',
                icon: 'warning'
            });
            return;
        }
        try {
            if (editingId) {
                await client.postApiSettingsCurrencyUpdate({
                    postApiSettingsCurrencyUpdateRequest: {
                        id: editingId,
                        name: formData.name,
                        symbol: formData.symbol,
                        active: formData.active
                    } as any
                });
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'success',
                    title: 'Currency has been updated successfully.',
                    showConfirmButton: false,
                    timer: 2000,
                    timerProgressBar: true
                });
            } else {
                await client.postApiSettingsCurrencyCreate({
                    postApiSettingsCurrencyCreateRequest: {
                        name: formData.name,
                        symbol: formData.symbol,
                        active: formData.active
                    } as any
                });
                Swal.fire({
                    title: 'Created!',
                    text: 'Currency has been created successfully.',
                    icon: 'success',
                    timer: 2000,
                    showConfirmButton: false
                });
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'success',
                    title: 'Currency has been created successfully.',
                    showConfirmButton: false,
                    timer: 2000,
                    timerProgressBar: true
                });
            }
            setIsCreateModalOpen(false);
            setIsEditModalOpen(false);
            setEditingId(null);
            setFormData({ name: '', symbol: '', active: true });
            refreshData();
        } catch (err) {
            console.error('Failed to save currency:', err);
            Swal.fire({
                title: 'Error',
                text: 'Failed to save currency',
                icon: 'error'
            });
        }
    };

    const handleEdit = async (item: any) => {
        setEditingId(item.id);
        setIsEditModalOpen(true);
        try {
            const res = await client.getApiSettingsCurrencyId({ id: item.id }) as any;
            let data = res.data?.data || res.data;
            if (Array.isArray(data)) {
                data = data[0];
            }
            if (data) {
                setFormData({
                    name: data.name || '',
                    symbol: data.symbol || '',
                    active: data.active !== undefined ? (typeof data.active === 'string' ? data.active === '1' : !!data.active) : true
                });
            }
        } catch (err) {
            console.error('Failed to fetch currency details:', err);
            // Fallback to item from list if API fails
            setFormData({
                name: item.name || '',
                symbol: item.symbol || '',
                active: item.active !== undefined ? item.active : true
            });
        }
    };

    const handleDelete = async (id: number) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                await client.postApiSettingsCurrencyDelete({
                    postApiSettingsCurrencyDeleteRequest: { id: id }
                } as any);
                Swal.fire(
                    'Deleted!',
                    'Currency has been deleted.',
                    'success'
                );
                refreshData();
            } catch (err) {
                console.error('Failed to delete currency:', err);
                Swal.fire({
                    title: 'Error',
                    text: 'Failed to delete currency',
                    icon: 'error'
                });
            }
        }
    };

    return (
        <div className="rounded-xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900 overflow-hidden shadow-sm">
            <div className="p-4 border-b border-gray-200 dark:border-gray-800 flex justify-between items-center bg-gray-50/50 dark:bg-white/[0.02]">
                <h3 className="text-lg font-bold text-gray-800 dark:text-white">Currencies</h3>
                <div className="flex items-center gap-2">
                    {loading && <span className="text-sm text-gray-500">Loading...</span>}
                    <button
                        onClick={() => {
                            setEditingId(null);
                            setFormData({ name: '', symbol: '', active: true });
                            setIsCreateModalOpen(true);
                        }}
                        className="px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 transition-colors text-sm font-medium"
                    >
                        Add Currency
                    </button>
                </div>
            </div>
            <div className="overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableCell isHeader className="px-5 py-3 text-start">Name</TableCell>
                            <TableCell isHeader className="px-5 py-3 text-start">Symbol</TableCell>
                            <TableCell isHeader className="px-5 py-3 text-start">Status</TableCell>
                            <TableCell isHeader className="px-5 py-3 text-end">Actions</TableCell>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {currencies.length === 0 ? (
                            <TableRow><TableCell colSpan={4} className="px-5 py-4 text-center text-gray-500">No currencies found.</TableCell></TableRow>
                        ) : (
                            currencies.map(item => (
                                <TableRow key={item.id} className="hover:bg-gray-50/50 dark:hover:bg-white/[0.01]">
                                    <TableCell className="px-5 py-4 font-medium text-gray-700 dark:text-gray-200">{item.name}</TableCell>
                                    <TableCell className="px-5 py-4">{item.symbol}</TableCell>
                                    <TableCell className="px-5 py-4">
                                        <Badge color={item.active ? 'success' : 'error'} size="sm">
                                            {item.active ? 'Active' : 'Inactive'}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="px-5 py-4 text-end">
                                        <div className="flex justify-end gap-3">
                                            <button
                                                onClick={() => handleEdit(item)}
                                                className="text-brand-500 hover:text-brand-700 transition-colors"
                                                title="Edit"
                                            >
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                </svg>
                                            </button>
                                            <button
                                                onClick={() => handleDelete(item.id)}
                                                className="text-red-500 hover:text-red-700 transition-colors"
                                                title="Delete"
                                            >
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                </svg>
                                            </button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>

            <Modal isOpen={isCreateModalOpen || isEditModalOpen} onClose={() => {
                setIsCreateModalOpen(false);
                setIsEditModalOpen(false);
            }} className="max-w-md p-0 overflow-hidden">
                <div className="flex flex-col h-full max-h-[90vh]">
                    {/* Fixed Header */}
                    <div className="p-6 border-b border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 sticky top-0 z-10">
                        <h3 className="text-lg font-bold text-gray-800 dark:text-white">{editingId ? 'Edit Currency' : 'Add Currency'}</h3>
                    </div>

                    {/* Scrollable Content */}
                    <div className="p-6 overflow-y-auto flex-grow">
                        <form id="currency-form" onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Name <span className="text-red-500">*</span></label>
                                <input
                                    type="text"
                                    name="name"
                                    required
                                    placeholder="US Dollar"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-700 outline-none focus:ring-2 focus:ring-brand-500/20"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Symbol <span className="text-red-500">*</span></label>
                                <input
                                    type="text"
                                    name="symbol"
                                    placeholder="$"
                                    required
                                    value={formData.symbol}
                                    onChange={handleInputChange}
                                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-700 outline-none focus:ring-2 focus:ring-brand-500/20"
                                />
                            </div>
                            <div className="space-y-1.5">
                                <label className="block text-sm font-semibold mb-1.5 text-gray-700 dark:text-gray-300">Status</label>
                                <select
                                    name="active"
                                    value={formData.active ? '1' : '0'}
                                    onChange={handleInputChange as any}
                                    className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-700 outline-none focus:ring-2 focus:ring-brand-500/20"
                                >
                                    <option value="1">Active</option>
                                    <option value="0">Inactive</option>
                                </select>
                            </div>
                        </form>
                    </div>

                    {/* Fixed Footer */}
                    <div className="p-6 border-t border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-white/[0.02] sticky bottom-0 z-10 flex justify-end gap-3">
                        <button
                            type="button"
                            onClick={() => {
                                setIsCreateModalOpen(false);
                                setIsEditModalOpen(false);
                            }}
                            className="px-4 py-2 text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700 font-medium transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            form="currency-form"
                            className="px-6 py-2 text-white bg-brand-500 rounded-lg hover:bg-brand-600 font-medium shadow-md shadow-brand-500/20 transition-all active:scale-95"
                        >
                            {editingId ? 'Save Changes' : 'Add Currency'}
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
}
